import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { PageHeader } from "./PageHeader";

const WIDTHS = {
  narrow: "max-w-screen-sm",
  default: "max-w-screen-xl",
  wide: "max-w-screen-2xl",
  full: "max-w-none",
} as const;

export type PageWidth = keyof typeof WIDTHS;

export interface PageProps {
  children?: ReactNode;
  /** Semantic content width. @default "default" */
  width?: PageWidth;
  /** Rendered full-bleed, before the gutter container. */
  outsideChildren?: ReactNode;
  /** Rendered full-bleed, after the gutter container. */
  bottomChildren?: ReactNode;
  /** Extra classes on the outer <main>. */
  className?: string;
  /** Extra classes on the inner container. */
  containerClassName?: string;
  /** Bottom padding reserving space for the fixed BottomNav. @default true */
  safeArea?: boolean;
}

const PageBase = ({
  children,
  width = "default",
  outsideChildren,
  bottomChildren,
  className,
  containerClassName,
  safeArea = true,
}: PageProps) => {
  const isFull = width === "full";

  const container = cn(
    "mx-auto w-full space-y-4 py-4 md:space-y-6",
    !isFull && "container px-4 md:px-6 lg:px-8",
    WIDTHS[width],
    containerClassName,
  );

  // Reserve space for the fixed BottomNav, which only shows below `lg`.
  const main = cn(safeArea && "pb-28 lg:pb-0", className) || undefined;

  return (
    <main className={main}>
      {outsideChildren}
      <div className={container}>{children}</div>
      {bottomChildren}
    </main>
  );
};

export const Page = Object.assign(PageBase, { Header: PageHeader });
